import {
  Chart,
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
 // BubbleController,
  DoughnutController,
  LineController,
 // PieController,
 // PolarAreaController,
 // RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip
} from 'chart.js';

import { getRelativePosition } from 'chart.js/helpers';

import { suggest_range } from './date_util';
import { Dataset } from './dataset';

import { ColorWheel } from './color_wheel';
import 'chartjs-adapter-date-fns';

import { formatNumberValue, formatDateValue } from '../format/format';
import { trendLine } from './trend_line_model';

Chart.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  
  BarController,
//  BubbleController,
  DoughnutController,
  LineController,
//  PieController,
//  PolarAreaController,
//  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip
);

class AbstractChart {

    constructor(element, type) {
    	this.element = element;
    	this.type = type;	
    	this.colorWheel = new ColorWheel();
    	
    	this.listeners = [];
    	
    	this.data = {
    		labels: undefined,
    		datasets: []
    	}
    	
    	this.formatTooltip = undefined;
    	
    	this.options = {
			responsive: true,
			maintainAspectRatio: false,
			resizeDelay: 250,
			plugins: {    			
				legend: {
					position: 'bottom'
				},
				tooltip: {
		        	intersect: false,
		            callbacks: {
		                label: ({dataset, raw}) => {
		                	if( raw ) {		                
			                	const { label }  = dataset;
			                	
			                    let textValue = label || '';
			                    
			                    if (textValue) {
			                        textValue += ': ';
			                    }
			                    
				            	let value = raw;
				            					            	
				            	if( dataset.formatValue ) {
				            		value = dataset.formatValue(value);
				            	} else if( this.formatTooltip ) {
				            		value = this.formatTooltip(value);
				            	} else if( typeof value === 'object' && value !== null && value.hasOwnProperty('y') ) {
				            		value = formatNumberValue(value.y);
				            	} 	            	
			                    
				            	textValue = `${textValue} ${value}`;
		
			                    if( dataset && dataset.unit ) {
			                    	textValue = `${textValue}${dataset.unit}`;  
			                    }
			                    
			                    return textValue;
		                    }
		                }
		            }
		        }
			},
			scales: {
				x: {
    				grid: {
    					drawOnChartArea: false,
    					drawTicks: true
	        		},
					ticks: {
						display: true,
						maxRotation: 0									
					}
				}
			}
		}    	
    }    	
    	    
    setLabels(labels) {
    	this.data.labels = labels;
    }
    
    setMaxX(max, tickFormat) {
    	this.setScaleX(0, max, tickFormat);
    }
    
    setScaleX(min, max, tickFormat) {
    	this.options.scales.x.type = 'linear'; 
    	this.options.scales.x.min = min;
    	this.options.scales.x.max = max;
    	
    	if( tickFormat ) {
    	  	this.options.scales.x.ticks.callback = tickFormat
	  	}
    }
    
    linearTimeScale(min_x, max_x) {
    	let startDate = new Date(min_x);
    	let endDate = new Date(max_x);
    	
    	let interval = suggest_range(startDate, endDate);
    	this.setScaleX(interval.min, interval.max);
    	    	
    	this.options.scales.x.type = 'time';
    	this.formatTooltip = ({x, y}) => {
    		let time = formatDateValue(new Date(x));
    		let value = formatNumberValue(y);
    		return `${time}: ${value}`;
    	};
    }
    
    addDataset(name, values, opt_options) {
    	let hex = this.colorWheel.next;
    	   
    	const dataset = {
			label: name,
			borderColor: hex || '#cccccc',
			backgroundColor: hex || '#cccccc',
			data: values,
			pointRadius: 0
		}
    	    	
    	Object.assign(dataset, opt_options || {})
    	    	
		this.data.datasets.push(dataset);
		return new Dataset( dataset );
	}
	
	addTrendLine(dataset, degree, period) {
		if( degree < 0 && degree > 10 ) { 
			console.warn("Invalid value for degree: ", degree);
			return;   
		} 	
		
		const { data, label, unit } = dataset.options;
    	const toValue = trendLine( data, degree, period );
    	
    	const toEntry = (x) => {
    		return {
    			x: x,
    			y: toValue(x)
    		}
    	};
    	
    	const trendValues = data.map( entry => toEntry(entry.x) );    
    	
    	this.addDataset(`${label} - trend`, trendValues, {
    		pointRadius: 0,
    		showLine: true,
    		borderDash: [10, 5],
    		unit: unit,
    		order: -10
    	});
	}
        
    buildScales() {
    	const data = this.data;
    	const undefinedUnit = "y";
    	
    	const mostCommonUnits = {}
    	data.datasets.map(dataset => dataset.unit)
    	.map(unit => !!unit ? unit : undefinedUnit)    	
    	.forEach(unit => { 
    	 		
    		if (unit in mostCommonUnits) {
    			mostCommonUnits[unit]++;
  			} else {
  				mostCommonUnits[unit] = 1;
  			}
  			
  			return mostCommonUnits;
    	});
    	
    	let primaryUnit = Object.keys(mostCommonUnits).reduce((a, b) => mostCommonUnits[a] > mostCommonUnits[b] ? a : b); 	
    	const scales = this.options.scales;
    	
    	Object.keys(mostCommonUnits).forEach(unit => {    	
    		let scaleKey = unit;
    		scales[scaleKey] = {
    			position: primaryUnit === unit ? 'left' : 'right',
    			weight: mostCommonUnits[unit],
    			ticks: {
    				callback: (value, index, values) => {
    					let label = formatNumberValue(value);
    					
    					if( unit !== undefinedUnit ) {
    						label = `${value}${unit}` 
    					}
    					
    					return label;
    				}
    			},
    			grid: {
            		display: primaryUnit === unit 
				}
    		}    		
    	});

    	data.datasets.forEach( dataset => {    		
    		if( dataset.unit !== null ) {
    			dataset.yAxisID = dataset.unit
    		} else {
    			dataset.yAxisID = 'y';
    		}
    	});
    }
    
    onMouseOver(xPos) {
	   this.listeners.forEach(f => f(xPos));
	}
    
    buildOptions() {
    	this.buildScales();
    	const my = this;
    	    
    	return {
    		type: this.type,
    		data: this.data,
    		options: this.options,
  			plugins: [{
    			id: 'myEventCatcher',
    			beforeEvent(chart, args, pluginOptions) {
	      			const event = args.event;
					if (event.type === 'mousemove') {
						const canvasPosition = getRelativePosition(event, chart);

						// Substitute the appropriate scale IDs
						const dataX = chart.scales.x.getValueForPixel(canvasPosition.x);
						my.onMouseOver(dataX);
					}
    			}    		
    		}]
    	}
	}
	        
	addMouseMoveListener(handler) {
		this.listeners.push(handler);
	}
	        
    build() {
    	const options = this.buildOptions();
    	this.instance = new Chart(this.element, options); 
    }
    
}


export {
	AbstractChart
}